/**
 * Phase 32 — one channel as the dialog edits it: the saved row plus a new
 * target typed over the masked one (empty = keep), the digest time as
 * HH:MM; and the body a save sends. The dirty check is a JSON compare of
 * the seeded draft and the edited one.
 * For Media Devoted | Created By Ciro Autiero
 */
import type { DropGenNotificationRow, DropGenNotificationTopic, DropGenNotificationUpdate } from '@trafficpulse/shared';
import { DROP_GEN_NOTIFICATION_TOPICS, normalizeTopics } from '@trafficpulse/shared';

import { hhmmToMinutes, minutesToHHMM } from '../../../utils/format';

export interface ChannelDraft {
  channel: DropGenNotificationRow['channel'];
  enabled: boolean;
  /** A new target; '' = keep the saved one. */
  target: string;
  hasTarget: boolean;
  targetMasked: string | null;
  /** Telegram: the bot token is set. Slack: always. */
  channelReady: boolean;
  /** Slack: a thread link is saved. */
  hasThread: boolean;
  /** A new thread link; '' = keep the saved one. */
  threadLink: string;
  topics: DropGenNotificationTopic[];
  /** Morning digest HH:MM; null = off. */
  digestAt: string | null;
  dayEndDigest: boolean;
  lastSentAt: string | null;
  lastError: string | null;
}

export function toDraft(row: DropGenNotificationRow): ChannelDraft {
  return {
    channel: row.channel,
    enabled: row.enabled,
    target: '',
    hasTarget: row.hasTarget,
    targetMasked: row.targetMasked,
    channelReady: row.channelReady,
    hasThread: row.hasThread,
    threadLink: '',
    topics: normalizeTopics(row.topics),
    digestAt: row.digestAtMin === null ? null : minutesToHHMM(row.digestAtMin),
    dayEndDigest: row.dayEndDigest,
    lastSentAt: row.lastSentAt,
    lastError: row.lastError,
  };
}

export function toBody(draft: ChannelDraft): DropGenNotificationUpdate {
  const target = draft.target.trim();
  const threadLink = draft.channel === 'SLACK' ? draft.threadLink.trim() : '';
  return {
    enabled: draft.enabled,
    topics: normalizeTopics(draft.topics),
    digestAtMin: draft.digestAt === null ? null : hhmmToMinutes(draft.digestAt),
    dayEndDigest: draft.dayEndDigest,
    ...(target ? { target } : {}),
    ...(threadLink ? { threadLink } : {}),
  };
}

/** What blocks a save, in the dialog's words; null when fine. */
export function draftProblem(draft: ChannelDraft): string | null {
  const name = draft.channel === 'TELEGRAM' ? 'Telegram' : 'Slack';
  if (draft.enabled && !draft.hasTarget && !draft.target.trim())
    return `${name}: add where the alerts go before switching it on.`;
  if (draft.channel === 'SLACK' && draft.enabled && !draft.hasThread && !draft.threadLink.trim())
    return "Slack: paste this country's thread link before switching it on — every country shares the one channel.";
  if (draft.enabled && draft.topics.length === 0 && draft.digestAt === null && !draft.dayEndDigest)
    return `${name}: pick at least one topic or a digest — switched on with nothing to send, it stays silent.`;
  return null;
}

export const toggleTopic = (topics: DropGenNotificationTopic[], topic: DropGenNotificationTopic): DropGenNotificationTopic[] =>
  topics.includes(topic) ? topics.filter((t) => t !== topic) : DROP_GEN_NOTIFICATION_TOPICS.filter((t) => t === topic || topics.includes(t));

export const isDirty = (a: ChannelDraft, b: ChannelDraft): boolean => JSON.stringify(a) !== JSON.stringify(b);
